"use client";
import {
  SearchInput,
  SearchProps
} from "@cmp-src/controls/SearchInput/SearchInput";
import { cn } from "@gettactic/helpers";
import { ScrollArea } from "@cmp-src/primitives/scroll-area";
import React, { RefObject, useMemo, useRef } from "react";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList } from "react-window";
import { VirtualizedListRow } from "./VirtualizedListRow";

export interface SearchableListProps
  extends React.PropsWithChildren<Record<never, never>> {
  title?: string;
  teamSearchQuery: string;
  setSearchQuery: SearchProps["setQuery"];
  actionElement: React.ReactNode;
  scrollAreaClassName?: string;
  scrollAreaRootClassName?: string;
  className?: string;
  maxRenderedItems?: number;
  itemSize?: number;
  itemClassName?: string;
  defaultPadding?: string;
}

export const VirtualizedSearchableList = ({
  title,
  teamSearchQuery,
  setSearchQuery,
  actionElement,
  children,
  scrollAreaClassName,
  scrollAreaRootClassName,
  className,
  maxRenderedItems = 50,
  itemSize = 92,
  itemClassName,
  defaultPadding = "px-1"
}: SearchableListProps) => {
  const firstItemRef = useRef<HTMLDivElement>(null);

  const childrenArray = useMemo(() => {
    return React.Children.toArray(children);
  }, [children]);

  const shouldVirtualize = childrenArray.length > maxRenderedItems;

  const renderHeader = () => (
    <>
      {title ? (
        <h3 className="py-1 font-bold text-lg text-secondary-foreground">
          {title}
        </h3>
      ) : null}
      <div className="flex flex-col justify-between gap-4 sm:gap-16 md:flex-row md:items-center">
        <div className="flex-1">
          <SearchInput
            fullWidth
            className="w-full"
            query={teamSearchQuery}
            setQuery={setSearchQuery}
          />
        </div>
        {actionElement}
      </div>
    </>
  );

  return (
    <div
      className={cn(
        "flex flex-1 flex-col flex-nowrap items-stretch gap-3 overflow-hidden",
        defaultPadding,
        className
      )}
    >
      {renderHeader()}

      {shouldVirtualize ? (
        <div
          className={cn(
            "h-full rounded-lg border border-neutral-300 min-h-[300px]",
            scrollAreaClassName
          )}
        >
          <AutoSizer disableWidth={false}>
            {(size: { width: number; height: number }) => (
              <FixedSizeList
                height={size.height}
                width={size.width}
                itemCount={childrenArray.length}
                itemSize={itemSize}
                itemData={{
                  childrenArray,
                  itemClassName,
                  firstItemRef: firstItemRef as RefObject<HTMLDivElement>
                }}
              >
                {VirtualizedListRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        </div>
      ) : (
        <ScrollArea
          className={cn(
            "flex-1 min-h-0 rounded-lg border border-neutral-300",
            scrollAreaClassName,
            scrollAreaRootClassName
          )}
        >
          <ul className={cn("divide-y divide-neutral-200", itemClassName)}>
            {children}
          </ul>
        </ScrollArea>
      )}
    </div>
  );
};
